import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, MenuItem, Slider, Stack, Switch, Tab, Tabs,
  TextField, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import StopIcon from '@mui/icons-material/Stop';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import { useAuth } from '../context/AuthContext';
import { isAdminUser } from '../utils/isAdminUser';
import { runFullSimulation, streamSimulation, fetchReplayTimeline } from '../utils/dynamanSimulation';
import DynaManStatsPanel from './DynaManStatsPanel';
import DynaManAnnualTable from './DynaManAnnualTable';
import DynaManLedgerTable from './DynaManLedgerTable';
import DynaManAuditTable from './DynaManAuditTable';
import DynaManLiveHud from './DynaManLiveHud';
import {
  DynaManLiveSimulatorDialog, DEBUG_ACCOUNT, REPLAY_TIMEFRAMES,
  SIGNAL_PROVIDERS, CTS_DIRECTION_RULES, DEFAULT_CTS_DIRECTION, TRENDPULSE_LEVERS,
  CTS_EXIT_LEVERS,
  CTS_ENTRY_LEVERS,
  DEFAULT_SIGNAL_PROVIDER,
  strategyFor, baselineFor, sandboxImplied, CTS_MECHANICS_OFF,
  EXIT_PROTECTION_LEVERS, COMPOSITE_ENGINES,
} from './DynaManLiveSimulator';
import {
  ENGINE_MODES, ENGINE_REQUIRED_MANDATE, ENGINE_FORCED_CTS_RULES,
} from '../utils/replaySession';
import { isProviderVisible } from '../config/featureFlags';

/**
 * THE DYNAMAN SIMULATOR: run the research engine, or watch it run.
 *
 * <h2>Two modes, one result shape</h2>
 *
 * A full run and a stepped run end with the same statistics, the same ledger and the same audit,
 * because the backend prepares both through one path. That is what makes the stepped view worth
 * having: it is not a demo of the engine, it is the SAME run slowed down, so a trade you watch
 * happen is a trade you can then find in the table.
 *
 * <h2>Admin only, and the server agrees</h2>
 *
 * A simulation is an unbounded read across a symbol's whole history and a stepped one holds a
 * server thread while somebody watches. The dialog is gated on {@code isAdminUser} and the
 * endpoints require authentication on their own authority — the gate here stops a control being
 * shown that would fail, it is not the permission.
 *
 * <h2>Stopping actually stops the server</h2>
 *
 * The stream is a `fetch` with an AbortController, and aborting closes the connection, which the
 * backend detects between bars and abandons the replay. A UI that merely stopped rendering would
 * leave a thread pacing through thousands of bars for nobody.
 */

/**
 * THE FIRST-LEVEL PROFIT TARGET CONTROL, hidden.
 *
 * <p>Hidden rather than deleted: the state, the request field and the engine lever all remain,
 * so the capability is intact and this constant is the whole of the restoration. The shipped CTS
 * strategy keeps it OFF regardless -- the first structural level sits a median 1.09R from a CTS
 * entry, and scaling out there measured +32.01% against PTB-only's +62.20%.
 */
const SHOW_FIRST_LEVEL_TARGET = false;

const DEFAULT_SYMBOL = 'SPY';
const DELAY_MARKS = [
  { value: 0, label: '0' },
  { value: 250, label: '250ms' },
  { value: 1000, label: '1s' },
  { value: 3000, label: '3s' },
  { value: 10000, label: '10s' },
];

/** Keeps the most recent `n`, newest first. */
const prepend = (list, item, n) => [item, ...list].slice(0, n);

/**
 * ONE RENDERER FOR ALL THREE LEVER BLOCKS -- entry gate, exit sandbox, profit protection.
 *
 * <p>Extracted rather than copied. The CTS-exit and profit-protection blocks already carried two
 * near-identical copies of this JSX, differing only in whether they set a test id; a third would
 * have made the next change to a lever row a three-place edit, which is how two of them come to
 * disagree about whether a disabled field is greyed or hidden.
 *
 * @param levers   a declarative list: {key, type, default, label, hint, enabledBy?, step?, min?}
 * @param values   the state object seeded from that list
 * @param onChange (key, value)
 * @param prefix   test-id prefix, so a control is addressable from a test and from a browser
 */
function LeverRows({ levers, values, onChange, running, prefix }) {
  return levers.map((lever) => (
    <Box key={lever.key} sx={{ mb: 0.5 }}>
      {lever.type === 'boolean' ? (
        <FormControlLabel
          control={(
            <Switch size="small" disabled={running || (lever.enabledBy && !values[lever.enabledBy])}
              data-testid={`${prefix}-${lever.key}`}
              checked={!!values[lever.key]}
              onChange={(e) => onChange(lever.key, e.target.checked)} />
          )}
          label={lever.label}
        />
      ) : (
        <TextField
          size="small" type="number" label={lever.label}
          data-testid={`${prefix}-${lever.key}`}
          // GREYED rather than hidden when its switch is off: the value is still what the run
          // will use the moment it is switched back on, and a field that vanishes takes that
          // information with it.
          disabled={running || (lever.enabledBy && !values[lever.enabledBy])}
          value={values[lever.key]}
          onChange={(e) => onChange(lever.key, Number(e.target.value))}
          inputProps={{ step: lever.step, min: lever.min }}
          sx={{ width: 190, ml: 3.5, mt: 0.5 }}
        />
      )}
      <Typography variant="caption" color="text.secondary"
        sx={{ display: 'block', ml: 3.75, mb: 0.75 }}>
        {lever.hint}
      </Typography>
    </Box>
  ));
}

const DynaManSimulatorDialog = ({ open, onClose, initialSymbol = '', onShowOnChart = null,
  onStartReplay = null, customSeries = null }) => {
  const { api, isAdmin, user, token } = useAuth();
  const [liveOpen, setLiveOpen] = useState(false);
  const allowed = isAdminUser(isAdmin, user);

  const [symbol, setSymbol] = useState(initialSymbol || DEFAULT_SYMBOL);
  /**
   * RE-SYNC THE SYMBOL TO THE CHART WHENEVER THE DIALOG OPENS.
   *
   * <p>`useState(initialSymbol)` runs its initialiser ONCE, on first mount. The dialog is mounted
   * for the life of the page and merely hidden, so after the first open the field held whatever
   * it held then -- SPY, for anyone who had not changed it -- while the chart moved on. A reader
   * looking at QQQ pressed Run and got SPY, which is the worst kind of wrong answer: a correct
   * one to a question nobody asked.
   *
   * <p>ON OPEN, not on every change of `initialSymbol`. Typing a different symbol here is a
   * deliberate act -- comparing one instrument against the chart is a legitimate thing to do --
   * and resyncing live would overwrite it mid-keystroke every time the chart loaded.
   */
  useEffect(() => {
    if (open && initialSymbol) setSymbol(initialSymbol);
  }, [open, initialSymbol]);
  const [delayMs, setDelayMs] = useState(250);
  /**
   * WHICH STORED SERIES THE RUN LOADS -- and it is on screen, which is the point.
   *
   * <h2>Why this exists</h2>
   *
   * Every path used to send `['daily']`, a constant nobody could see. That made the intraday
   * trailing-floor bailout unable to fire on ANY run from this app, and the engine said so on
   * every result -- "none of the bailout series 30m, 65m are present ... This is a limit of the
   * data". It is not: SPY, QQQ, DIA, TSLA and JNJ all have 30m and 65m on disk. It was a limit
   * of the REQUEST, and the warning sent a reader looking for missing files.
   *
   * <h2>ALL SERIES is the default, because that is what the record was measured on</h2>
   *
   * Every published figure in CLAUDE.md comes from a run with all four series loaded. Defaulting
   * to daily meant the dialog could not reproduce its own documentation -- and a stop can only
   * be touched mid-session when the intraday bars exist, so the two bar sets give genuinely
   * different fills, not a rounding difference.
   *
   * <p>The REPLAY is daily-only whatever this says, and its control states that: one frame per
   * candle is the only arrangement in which Step moves the chart. Stated in the UI rather than
   * hidden in a constant, which is the whole difference from the version this replaces.
   */
  const [barSet, setBarSet] = useState('all');
  const timeframesFor = (mode) => (mode === 'replay' || barSet === 'daily'
    ? REPLAY_TIMEFRAMES : undefined);
  /**
   * WHICH PROVIDER EVERY RUN IN THIS DIALOG USES.
   *
   * <p>Held here rather than inside the live view, because all four paths — full run, replay,
   * stream and the live dialog — must agree. {@link DEMO_STRATEGY} now carries
   * `disableStructuralEntry`, so the provider is the ONLY thing that can open a premise: without
   * this the dialog would send no provider at all and every run would be inert.
   *
   * <p><b>Seeded from {@link DEFAULT_SIGNAL_PROVIDER}, not from a literal.</b> This used to
   * hardcode CTS while the live view used the shared constant, so the two could disagree about
   * what a reader saw on open -- the exact defect class this dialog has already shipped twice.
   * One declaration now, and changing the audited default is a one-line change in one place.
   */
  const [signalProvider, setSignalProvider] = useState(DEFAULT_SIGNAL_PROVIDER);

  /**
   * THE EXECUTION ENGINE, selectable here and not only in the Workbench.
   *
   * <h2>Why it belongs in THIS dialog as well</h2>
   *
   * The Workbench picks an engine and replays it bar by bar; this is where a run produces a
   * LEDGER and an ANNUAL SUMMARY. The Timing Engine's whole purpose is to generate those from
   * one selection, so an engine dropdown that existed only in the other dialog would mean the
   * mode could be chosen nowhere near the report it exists to produce.
   *
   * <p>LEGACY by default, which is what every run from this dialog has always been — so nothing
   * about an existing comparison moves.
   */
  const [engineMode, setEngineMode] = useState('LEGACY');

  /**
   * Selecting an engine that REQUIRES a mandate takes it.
   *
   * <p>The Timing Engine is the only one, and the backend refuses the mismatch outright — so
   * without this a reader who picked it on a PTA run would get a 400 rather than a run. Switched
   * where the engine is chosen, so the two dropdowns cannot be left disagreeing.
   */
  const chooseEngine = (value) => {
    setEngineMode(value);
    const required = ENGINE_REQUIRED_MANDATE[value];
    if (required && required !== signalProvider) {
      setSignalProvider(required);
    }
    /*
     * MATERIALISE THE RULES THE ENGINE OWNS into the controls that show them.
     *
     * The backend forces them either way, so the RUN is correct without this. The DIALOG was
     * not: selecting the Timing Engine left the direction dropdown reading "SMA pullback" over a
     * run using the inflection vector. Found by reading the dropdown in a browser, and it is the
     * same invisible divergence the engine presets were given this treatment for.
     *
     * Written after `setSignalProvider`, because selecting CTS applies that provider's baseline
     * and would otherwise overwrite these a moment later.
     */
    const forced = ENGINE_FORCED_CTS_RULES[value];
    if (forced) {
      setCtsDirection(forced.ctsDirection);
      setCtsEntry((prev) => ({
        ...prev,
        ctsVectorLookback: forced.ctsVectorLookback,
        ctsPriceConfirmation: forced.ctsPriceConfirmation,
      }));
      setCtsExit((prev) => ({ ...prev, ...CTS_MECHANICS_OFF, ctsExitSandbox: false }));
      // AND THE BAR SET, which the backend also forces now. Without this the control would read
      // "All stored series" over a run the engine restricted to daily -- the same screen-says-
      // one-thing-ledger-says-another divergence the rules above are materialised for, and the
      // one with the largest effect on the headline of any control in this dialog.
      if (forced.barSet) {
        setBarSet(forced.barSet);
      }
      // AND THE STRUCTURAL TARGETS. `targetPtb` defaults ON -- it is the measured default for a
      // standalone CTS run -- so a Timing run inherited a profit target the mode does not have.
      if (forced.targetPta !== undefined) setTargetPta(forced.targetPta);
      if (forced.targetPtb !== undefined) setTargetPtb(forced.targetPtb);
    }
  };

  /**
   * True while the selected engine owns the BAR SET. Separate from the CTS-rules flag because
   * the two are not the same claim: a future engine could own one and not the other, and reading
   * the barSet key directly is what keeps the control and the declaration in step.
   */
  const engineOwnsBarSet = !!(ENGINE_FORCED_CTS_RULES[engineMode] || {}).barSet;

  /**
   * True while the selected engine owns the structural profit targets.
   *
   * <p>Read as a KEY PRESENCE rather than as a truthy value: the forced value is `false`, so
   * `!!forced.targetPtb` would be false and the control would stay editable over a run that
   * overrides it -- which is the stale-switch defect in its most deceptive form, because the
   * switch would be showing the right value and still be a lie about who chose it.
   */
  const engineOwnsTargets = (ENGINE_FORCED_CTS_RULES[engineMode] || {}).targetPtb !== undefined;

  /** True while the selected engine owns the CTS rules, so their controls are read-only. */
  const engineOwnsCtsRules = !!ENGINE_FORCED_CTS_RULES[engineMode];
  const [showNarratives, setShowNarratives] = useState(true);
  /**
   * THE SECOND CHANCE, off by default — which is the engine default and the measured one.
   *
   * <p>Measured on daily SPY: 147 re-entries at a 23.8% win rate and PF 0.55, taking the book
   * from +40.44% gross to -35.37% and more than doubling the drawdown. It is a research toggle,
   * so it is offered and it is not on.
   */
  /**
   * WHICH WAY a CTS timing point points. The engine's own default.
   *
   * <p>Offered for the visual audit, not as a menu of improvements: every rule measured here is
   * weak and none beats a coin flip on the full signal set. The hints on each option carry what it
   * measured, so a reader who switches and sees a worse result can see that was expected.
   */
  const [ctsDirection, setCtsDirection] = useState(DEFAULT_CTS_DIRECTION);
  /**
   * TRENDPULSE'S SHAPE-DETECTION LEVERS, keyed by the API parameter they become.
   *
   * <p>Held as one object rather than three states so a lever added to {@link TRENDPULSE_LEVERS}
   * appears in the dialog without a matching `useState` — the list is the single declaration.
   *
   * <p>Seeded from each lever's own default, so an untouched dialog sends exactly what the engine
   * would have used anyway.
   */
  const [tpLevers, setTpLevers] = useState(
    () => Object.fromEntries(TRENDPULSE_LEVERS.map((l) => [l.key, l.default])));
  const [tpDriftFilter, setTpDriftFilter] = useState(true);
  const [ctsReentry, setCtsReentry] = useState(false);
  /**
   * PTA/PTB SCALE-OUT, off by default to match the engine.
   *
   * <p>Measured on daily SPY: 36 of 260 trades reach PTA and ALL 36 finish profitably, taking the
   * book from +40.44% to +53.50% gross and dropping the drawdown. Unlike the re-entry toggle
   * beside it, this one improves the result — so it is ON here and ON in the engine, the only
   * research lever promoted to a default.
   */
  /**
   * THE TWO STRUCTURAL TARGETS, independently switchable.
   *
   * <p>PTA is the first swing level beyond the entry and PTB the second — real prices the market
   * turned at, read through the engine's `StructuralLevelSource`. They are two states rather than
   * one because the three live combinations are three different strategies, not one feature at
   * three strengths:
   *
   *   PTA only  — half comes off at the first level, the rest is trailed
   *   PTB only  — the first level is ignored, 100% held and 100% exited at the second
   *   both      — half at the first, the remainder at the second
   *
   * <p><b>PTA off and PTB on is the shipped strategy</b>, matching the engine's own defaults, and
   * it is a measured choice: the first structural level sits a median 1.09R from a CTS entry, so
   * scaling out there destroys the payoff asymmetry. PTB-only measured +62.20% gross / PF 1.44
   * against +32.01% for PTA-only on daily SPY.
   */
  /**
   * THE TWO BAND ENTRY FILTERS, both off — which is the behaviour every band figure was measured
   * under, so nothing moves until one is asked for.
   *
   * <h2>Why they are on screen at all</h2>
   *
   * The Engine State panel shows "Macro regime (50/200 SMA)" and "Volume confluence" greyed out on
   * a band run, because the provider path bypasses the first and never consulted the second. A
   * filter a reader can see but cannot switch on is untestable, and the question "would it help"
   * is a measurement rather than an argument.
   *
   * <p><b>Measured on daily SPY, and the answer is interesting:</b>
   *
   * <pre>
   *   neither (default)        242 trades   -12.18% gross   PF 1.00   net -11.98%
   *   macro gate only          214          +12.33%         PF 1.10   net  -0.01%
   *   volume only              178          -32.37%         PF 0.89   net -18.52%
   *   BOTH                     135          +40.54%         PF 1.28   net +13.90%
   * </pre>
   *
   * Volume alone HURTS and volume on top of the macro gate helps a great deal, so the two are not
   * additive — which is exactly why both are exposed rather than one being promoted.
   *
   * <p>They are left OFF by default so the demonstrator still reproduces the published baseline.
   * Four cells on one symbol is a lead, not a result.
   */
  const [macroGate, setMacroGate] = useState(false);
  const [bandVolumeConfluence, setBandVolumeConfluence] = useState(false);

  /**
   * THE CTS EXIT SANDBOX, as ONE state object keyed by the API parameter each lever becomes.
   *
   * <p>One object rather than sixteen `useState` calls, and seeded from {@link CTS_EXIT_LEVERS} --
   * so a lever added to that list appears in the dialog, reaches all four run paths and defaults
   * correctly without a matching line anywhere else. Sixteen separate states is sixteen chances
   * to wire fifteen.
   *
   * <p>Every default is the ENGINE's default, so an untouched dialog sends exactly what the
   * backend would have used anyway and the locked CTS baseline reproduces from a run nobody
   * configured.
   */
  const [ctsExit, setCtsExit] = useState(
    () => Object.fromEntries(CTS_EXIT_LEVERS.map((l) => [l.key, l.default])));

  /**
   * THE CTS ENTRY GATE, seeded from {@link CTS_ENTRY_LEVERS} for the same reason the two blocks
   * below it are: one object rather than five states, so a lever added to that list appears in
   * the dialog, reaches all four run paths and defaults correctly with no second edit.
   *
   * <p>Unlike those blocks, these defaults are NOT all the engine's. The gate ships false in
   * `DynaManRuleConfig` to keep the locked CTS ledger reproducible from a bare CLI run, and TRUE
   * here because it is the better-measured configuration. So an untouched dialog deliberately
   * does NOT reproduce the locked baseline -- see the block's own caption, which says so on
   * screen rather than leaving it to be discovered from a trade count.
   */
  const [ctsEntry, setCtsEntry] = useState(
    () => Object.fromEntries(CTS_ENTRY_LEVERS.map((l) => [l.key, l.default])));
  const setCtsEntryLever = (key, value) =>
    setCtsEntry((prev) => ({ ...prev, [key]: value }));

  /**
   * PROFIT PROTECTION, seeded from {@link EXIT_PROTECTION_LEVERS} for the same reason the CTS
   * block is: one object rather than six states, so a lever added to that list appears in the
   * dialog, reaches all four run paths and defaults correctly with no second edit.
   */
  const [protection, setProtection] = useState(
    () => Object.fromEntries(EXIT_PROTECTION_LEVERS.map((l) => [l.key, l.default])));
  const setProtectionLever = (key, value) =>
    setProtection((prev) => ({ ...prev, [key]: value }));

  /**
   * Which engines a COMPOSITE run includes. All three by default, matching the backend's reading
   * of an absent list.
   */
  const [compositeEngines, setCompositeEngines] = useState(
    () => Object.fromEntries(COMPOSITE_ENGINES.map((e) => [e.key, true])));
  const isComposite = signalProvider === 'COMPOSITE';
  /**
   * The wire form: a comma-separated list of the ticked engines.
   *
   * <p>Sent only for a composite run, and sent even when EMPTY -- an empty list is a different
   * request from an absent one, and the backend refuses it with its own message rather than
   * reporting a flat ledger as a result.
   */
  const compositeEnginesParam = isComposite
    ? COMPOSITE_ENGINES.filter((e) => compositeEngines[e.key]).map((e) => e.key).join(',')
    : undefined;
  /**
   * SETTING A SUB-MECHANIC FORCES THE MASTER ON.
   *
   * <p>The panel said the master was "implied by any switch below" and it was not: the dialog
   * sends `ctsExitSandbox` explicitly on every request, and an explicit false beats the
   * backend's own implication. Turning MACD on with the master visually off produced a run that
   * ignored MACD and reported the default sandbox result as if it were the crossover's.
   *
   * <p>Forced here so the PANEL cannot lie, and recomputed again in the payload so the REQUEST
   * cannot either — see {@link sandboxImplied}. Never forced OFF: the master also governs the
   * breathable stop, so clearing the last mechanic must not silently change the stop as well.
   */
  const setCtsLever = (key, value) => setCtsExit((prev) => {
    const next = { ...prev, [key]: value };
    // SWITCHING THE MASTER OFF TURNS OFF EVERYTHING UNDER IT, and this is the regression fix.
    //
    // Without it the implication below fought the click: selecting CTS applies a baseline with
    // the time ejector ON, the ejector is an implier, so turning the master off re-implied it
    // immediately and the switch snapped back. The exit manager was effectively mandatory, and
    // RATCHETING_GATES -- which has its own stop, breakeven and trail, and is what the
    // profitable SPY baseline was measured under -- became unreachable from this dialog.
    if (key === 'ctsExitSandbox' && !value) {
      return { ...next, ...CTS_MECHANICS_OFF, ctsExitSandbox: false };
    }
    // Turning a mechanic ON still forces the master on: the panel must not show a mechanic
    // enabled under a master that would make the backend ignore it.
    return sandboxImplied(next) ? { ...next, ctsExitSandbox: true } : next;
  });

  /**
   * APPLY THE PROVIDER'S MEASURED BASELINE WHEN THE DROPDOWN CHANGES.
   *
   * <p>Switching engine used to leave every toggle where the previous one had it, so a reader
   * moving from CTS to PTA ran PTA with CTS's settings and compared the result against figures
   * measured under neither. {@link PROVIDER_BASELINES} carries what each engine measured best
   * at, and it is reapplied on every change — see that declaration for the numbers and for why
   * overwriting a manual edit at this boundary is the right behaviour.
   *
   * <p>Keyed on `signalProvider` alone. Adding the state it writes to the dependency array would
   * make it re-run on its own output, which is a loop that ends with the reader unable to change
   * a toggle at all.
   */
  useEffect(() => {
    const baseline = baselineFor(signalProvider);
    const forced = ENGINE_FORCED_CTS_RULES[engineMode];
    if (!baseline && !forced) return;
    if (baseline) setMacroGate(baseline.macroGate);
    setCtsExit((prev) => ({
      ...prev,
      ...(baseline ? baseline.ctsExit : {}),
      /*
       * AND THE ENGINE WINS, resolved HERE rather than only in `chooseEngine`.
       *
       * This is where the Time ejector came back on. `chooseEngine` applied
       * CTS_MECHANICS_OFF and then set the provider to CTS, and its comment claimed the
       * ordering handled it -- but setter order inside a handler does not beat an EFFECT,
       * which runs after the commit. So the CTS baseline (ejector ON at 5 bars) was applied
       * last and the switch a reader saw was on, over a run the backend had forced it off
       * for. The run was right and the screen was not, which is the divergence this dialog
       * materialises forced values to prevent.
       *
       * Keyed on engineMode as well, so it also holds when the PROVIDER is changed while a
       * mode that owns the rules is selected.
       */
      ...(forced ? { ...CTS_MECHANICS_OFF, ctsExitSandbox: false } : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalProvider, engineMode]);
  const isCtsProvider = signalProvider === 'CTS';
  /**
   * THE CTS LEVERS AS SENT: the master recomputed, and the ejector's bars zeroed when it is off.
   *
   * <p>Two corrections the raw state cannot make. The master must be true whenever a mechanic
   * implies it, whatever the switch shows; and the time ejector's BARS are what the engine
   * reads, so an ejector switched off has to travel as 0 or it keeps firing at 5 — exactly the
   * "quietly interfering" the switch was added to stop.
   */
  const ctsPayload = () => {
    const sandbox = ctsExit.ctsExitSandbox || sandboxImplied(ctsExit);
    // MASTER OFF SENDS EVERY CHILD OFF, explicitly.
    //
    // Belt and braces over the explicit `ctsExitSandbox=false`, which the backend already
    // honours ahead of its own implication. A stale `ctsMaxHoldBars: 5` riding along would be
    // inert today and would silently switch the sandbox back on the moment anything upstream
    // started inferring from the mechanics again -- and a run reading as RATCHETING_GATES while
    // the ejector fired is the exact failure being fixed.
    if (!sandbox) {
      return { ...ctsExit, ...CTS_MECHANICS_OFF, ctsExitSandbox: false };
    }
    return {
      ...ctsExit,
      ctsExitSandbox: true,
      // The engine reads BARS, so an ejector switched off has to travel as 0.
      ctsMaxHoldBars: ctsExit.ctsTimeEjectorEnabled ? ctsExit.ctsMaxHoldBars : 0,
    };
  };

  /**
   * ISOLATE THE SELECTED PROVIDER, on — which is how every per-provider figure was measured.
   *
   * <p>Off, the engine runs the chosen provider AND the Donchian structural breakout together, and
   * CLAUDE.md's own note on that pair applies: a run with both attached is a run of neither. It is
   * a switch rather than a constant because "what do the band entries look like alongside the
   * breakout" is a legitimate question, and because a value hardcoded in the request is a value
   * nobody can see.
   */
  const [isolateProvider, setIsolateProvider] = useState(true);

  const [targetPta, setTargetPta] = useState(false);
  const [targetPtb, setTargetPtb] = useState(true);
  /**
   * HOW FAR a level must be from the entry, in R, to be usable at all.
   *
   * <p>2.0, matching the engine. A floor on ASYMMETRY rather than a tuning knob: on SPY the first
   * structural level sits a median 1.09R from the entry with 43% of them inside 1R, and a target
   * nearer than the trade's own risk cannot produce a favourable payoff however often it is hit.
   *
   * <p>Zero accepts any level and is what reproduces figures recorded before 2026-10-02 — every
   * configuration measured without a floor lost to having no targets at all.
   */
  const [levelMinR, setLevelMinR] = useState(2);
  /**
   * Whether the chosen provider is a time-measurement system.
   *
   * <p>PTA and PTB run NAKED: the server forces the CTS signal reversal and both structural
   * profit targets off, because those were tuned for CTS and were measured at 17-28 points of
   * gross in the wrong direction on the first band baselines. The controls for them are therefore
   * hidden rather than shown-but-ignored.
   *
   * <p>Derived rather than stored, so it cannot fall out of step with `signalProvider`.
   */
  const isTimeBandProvider = signalProvider === 'PTA' || signalProvider === 'PTB';
  /**
   * WHICH PROVIDERS HAVE A MACRO GATE A SINGLE SWITCH CAN EXPRESS.
   *
   * <p>PTA and PTB read `timeBandBypassMacroVeto`; CTS reads `ctsBypassMacroVeto`. Both are a
   * property of the RUN, so one switch says it.
   *
   * <p>TrendPulse is absent and that is not an oversight: it grants the override per SHAPE --
   * capitulation drops and exhaustion spikes carry it, M and W shapes do not, because a
   * capitulation happens in a downtrend and that is what capitulation IS. A run-level switch
   * cannot express a per-shape rule, and a control that silently meant something else on one
   * provider would be worse than an absent one.
   */
  const hasMacroGate = isTimeBandProvider || signalProvider === 'CTS';
  const [tab, setTab] = useState(0);

  const [running, setRunning] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loadingReplay, setLoadingReplay] = useState(false);

  const [liveBar, setLiveBar] = useState(null);
  const [liveTransitions, setLiveTransitions] = useState([]);
  const [liveRejections, setLiveRejections] = useState([]);

  const abortRef = useRef(null);

  /* ── lifecycle ─────────────────────────────────────────────────────────────────────────── */

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  // A dialog closed mid-stream must release the server's thread. Without this, closing the window
  // leaves a stepped run pacing through the rest of the series for nobody.
  useEffect(() => {
    if (!open) stop();
  }, [open, stop]);

  useEffect(() => () => stop(), [stop]);

  const resetRun = () => {
    setError('');
    setResult(null);
    setWarnings([]);
    setLiveBar(null);
    setLiveTransitions([]);
    setLiveRejections([]);
  };

  /* ── full run ──────────────────────────────────────────────────────────────────────────── */

  /**
   * EVERY RUN IN THIS DIALOG SENDS {@code strategyFor(signalProvider)}, and omitting the strategy
   * was a real defect.
   *
   * <p>All three calls here used to pass `{ symbol }` alone. The backend then fell to the engine
   * default, `ATR_TRAILING_FLOOR` — which is gated on a 20 EMA break AND restricted to the 30m/65m
   * series. SPY's intraday files begin 2020-02-10 while its daily history starts 1993, so for the
   * first twenty-five years of a 1995 premise there was not one bar the floor was allowed to act
   * on, and the static entry stop was the only live exit. The replay drew a single trade as a
   * straight line from 1995 to 2020-02-25 — fifteen days after intraday data begins, which is the
   * mechanism rather than a coincidence.
   *
   * <p>Worse, `DynaManLiveSimulator` in this same dialog has always sent the trendline exit. So
   * two features a click apart ran different strategies, and only one of them matched the
   * published numbers.
   *
   * <p>Spread rather than hardcoded, and FIRST, so a caller-supplied override still wins.
   *
   * <h2>AND IT MUST SEND {@code timeframes}, which is what the autorun discrepancy was</h2>
   *
   * This call omitted it while {@link startReplay} has always sent {@code REPLAY_TIMEFRAMES}, so
   * the backend replayed EVERY stored series for the symbol — 169,144 bars for SPY against the
   * 8,466 daily ones. The reported figures differed accordingly: 160 trades and +28.54% against
   * the daily run's 159 and +34.87%.
   *
   * <p>IT WAS NOT PROVIDER CONTAMINATION. The suspicion was that CTS signals were leaking into a
   * PTA run; the intent breakdown settles it — 189 {@code A-ESC-B}, 12 {@code A-RUN-B}, 1
   * {@code A-FLP-B} and nothing else, with no CTS code present at any count. The engine attaches
   * exactly one provider by mode and {@code PtaIsolationTest} pins that.
   *
   * <p>What the extra series change is the FILL, not the signals. With intraday bars present a
   * stop can be touched mid-session and the gap rule fills at that bar's own worse open; on daily
   * bars the same breach usually fills at the stop. So two views of one strategy disagreed about
   * the result while agreeing about every decision, which is the harder kind of divergence to
   * see — and the whole dialog now asks for one bar set, so the scorecard and the chart beneath it
   * describe the same run.
   */
  const runFull = async () => {
    resetRun();
    setRunning(true);
    try {
      const data = await runFullSimulation(api,
        { ...strategyFor(signalProvider), signalProvider, engineMode, ctsDirection, ...tpLevers, tpDriftFilter,
          // THE SAME ACCOUNT THE REPLAY SENDS. Without it this run was UNSIZED and could only
          // report the gross compounded figure, while the replay -- which has always spread
          // DEBUG_ACCOUNT -- reported the sized net one. Two correct numbers for one run with
          // nothing on screen to say they measured different things, which read as the two
          // surfaces disagreeing. The account runs AFTER the engine on a finished ledger, so it
          // cannot move a single trade: the gross figures are unchanged and the net ones appear.
          ...DEBUG_ACCOUNT,
          ctsReentry, targetPta, targetPtb, levelMinR, timeframes: timeframesFor('full'),
          macroGate, bandVolumeConfluence, ...ctsPayload(), ...ctsEntry, ...protection, compositeEngines: compositeEnginesParam, disableStructuralEntry: isolateProvider, symbol,
          customSeries });
      setResult(data);
      setWarnings(data.reachability || []);
      setTab(0);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'The simulation failed');
    } finally {
      setRunning(false);
    }
  };


  /* ── the replay ────────────────────────────────────────────────────────────────────────── */

  /**
   * Fetch the bar-by-bar timeline and hand it to the chart, then get out of the way.
   *
   * <p>A separate request from Run Full Simulation rather than a field on it: the timeline is
   * about half a megabyte of frames that only the replay reads, and making every summary run
   * carry it would slow the common case for a feature most runs never use.
   */
  const startReplay = async () => {
    setError('');
    setLoadingReplay(true);
    try {
      const timeline = await fetchReplayTimeline(api, {
        ...strategyFor(signalProvider),
        ctsDirection,
        engineMode,
        ...tpLevers,
        tpDriftFilter,
        ctsReentry,
        macroGate,
        bandVolumeConfluence,
        ...ctsPayload(), ...ctsEntry, ...protection, compositeEngines: compositeEnginesParam,
        disableStructuralEntry: isolateProvider,
        targetPta,
        targetPtb,
        levelMinR,
        ...DEBUG_ACCOUNT,
        // SIGNAL SERIES ONLY, and this is what makes "Step" mean one candle.
        //
        // Without it the backend replays every stored series, so on SPY most frames are 5-minute
        // bars. Stepping then advances the FSM one BAR while the daily chart underneath does not
        // move for ~78 of them - the playhead sits on the same candle and the panel looks frozen.
        // One frame per candle is the only arrangement in which the chart and the HUD can move
        // together, which is the whole premise of stepping through a trade.
        //
        // The cost is real and bounded: no intraday bailout can fire, so a trendline or trail
        // exit lands on a daily close rather than at 10:00. On SPY daily that still produces 135
        // trades with all four exit reasons, so the gates are fully exercised. For the intraday
        // behaviour use the CLI (`--explain`) or an unrestricted /api/simulation/full run.
        // ALWAYS daily: one frame per candle is the only arrangement in which Step
        // moves the chart. The control says so rather than leaving it to be noticed.
        timeframes: timeframesFor('replay'),
        // THE CUSTOMER'S OWN SERIES, when one is loaded. Null on every ordinary run, and the
        // helper switches to POST only when it is present -- so this single key is what makes
        // all three readings available on uploaded data, and a path that omitted it would
        // silently run the STORED symbol of the same name instead.
        customSeries,
        // ONE PROVIDER, whichever the toolbar selects. The others are not attached at all — not
        // attached-and-ignored — so their detectors never run and nothing they would have raised
        // appears in the audit. This was hardcoded to CTS while the rest of the dialog sent no
        // provider and ran the Donchian breakout; the selector is what makes all four paths agree.
        //
        // `disableStructuralEntry` travels in the shared strategy, so it is not repeated here:
        // mixing two entry sources in one run makes the result a measurement of neither.
        signalProvider,
        // EVERY BAR, not a window. A windowed replay starts partway into the run, so the ledger
        // opens already carrying every trade that closed before the window — "100 trades closed"
        // before a single click — and it ends partway too, never reaching the current data edge.
        // Zero means no cap; on daily SPY that is 8,466 frames rather than 2,000.
        maxBars: 0,
        symbol,
      });
      onStartReplay(timeline);
      // Closes, because the chart it replays is behind this dialog. Leaving it open would put
      // the answer underneath the question.
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'The replay timeline could not be loaded');
    } finally {
      setLoadingReplay(false);
    }
  };

  /* ── stepped run ───────────────────────────────────────────────────────────────────────── */

  const runStepped = async () => {
    resetRun();
    setRunning(true);
    setStreaming(true);
    setTab(3);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamSimulation(
        async () => token,
        // timeframes, for the same reason runFull now sends it: all three paths must ask for ONE
        // bar set, or the stepped view, the replay and the scorecard each describe a different run
        // of the same strategy.
        { ...strategyFor(signalProvider), signalProvider, engineMode, ctsDirection, ...tpLevers, tpDriftFilter,
          ctsReentry, targetPta, targetPtb, levelMinR, timeframes: timeframesFor('stream'),
          macroGate, bandVolumeConfluence, ...ctsPayload(), ...ctsEntry, ...protection, compositeEngines: compositeEnginesParam, disableStructuralEntry: isolateProvider, symbol, delayMs,
          customSeries },
        {
          onStart: (payload) => setWarnings(payload.warnings || []),
          onBar: (bar) => setLiveBar(bar),
          onTransition: (t) => setLiveTransitions((prev) => prepend(prev, t, 40)),
          onRejection: (r) => setLiveRejections((prev) => [...prev, r]),
          onComplete: (payload) => {
            // The stepped run ends with the same shape a full run returns, so every tab below
            // works identically for both. `reachability` is carried from the start event.
            setResult({
              symbol,
              statistics: payload.statistics,
              completedTrades: payload.completedTrades || [],
              openTrade: payload.openTrade || null,
              rejectedSignals: [],
              rejectionCounts: payload.rejectionCounts || {},
              meta: null,
            });
          },
          onError: (message) => setError(message),
        },
        controller.signal
      );
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError(e.message || 'The simulation stream failed');
      }
    } finally {
      setStreaming(false);
      setRunning(false);
      abortRef.current = null;
    }
  };

  /* ── render ────────────────────────────────────────────────────────────────────────────── */

  // The audit shown is whichever is populated: a full run returns the whole log, a stepped run
  // accumulates only what it streamed (the warm-up is deliberately silent).
  const auditRows = result?.rejectedSignals?.length ? result.rejectedSignals : liveRejections;
  const auditCounts = result?.rejectionCounts || null;
  const totalDeclined = auditCounts
    ? Object.values(auditCounts).reduce((a, b) => a + b, 0)
    : auditRows.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { height: '92vh' } }}>
      <DialogTitle>
        DynaMan Simulator
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Offline research engine — reads stored bars, trades nothing.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {!allowed ? (
          <Alert severity="info">
            The DynaMan simulator is an administrator tool.
          </Alert>
        ) : (
          <>
            {/* ── controls ───────────────────────────────────────────────────────────── */}
            <Stack direction="row" spacing={2} alignItems="center"
              sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <TextField
                label="Symbol"
                size="small"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                disabled={running}
                sx={{ width: 140 }}
              />
              {/*
                THE PROVIDER, selectable here because every run in this dialog now ISOLATES it —
                DEMO_STRATEGY carries `disableStructuralEntry`, so this choice is the only thing
                that can open a premise. Before, the dialog sent no provider at all and ran the
                Donchian breakout, which matched none of the published per-provider figures.
              */}
              <TextField
                select
                label="Signal provider"
                size="small"
                value={signalProvider}
                onChange={(e) => setSignalProvider(e.target.value)}
                disabled={running}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 190 }}
              >
                {/* PARKED PROVIDERS ARE NOT OFFERED. TrendPulse (and CONFLUENCE, which IS
                  TrendPulse with a CTS gate in front of it) are hidden for the launch and the
                  engine still runs both -- see config/featureFlags.js. */}
              {SIGNAL_PROVIDERS.filter((p) => isProviderVisible(p.value)).map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </TextField>
              <TextField
                select
                label="Execution engine"
                size="small"
                value={engineMode}
                onChange={(e) => chooseEngine(e.target.value)}
                disabled={running}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'dynaman-engine-mode' }}
                sx={{ width: 190 }}
              >
                {ENGINE_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </TextField>
              {/*
                THE CTS DIRECTION RULE. Shown only for runs that can raise a CTS intent -- under
                TRENDPULSE it would be a control with no effect, which is worse than an absent one.
              */}
              {(signalProvider === 'CTS' || signalProvider === 'CONFLUENCE') && (
                <TextField
                  select
                  label="CTS direction"
                  size="small"
                  value={ctsDirection}
                  onChange={(e) => setCtsDirection(e.target.value)}
                  // READ-ONLY while the engine owns it. A field a reader can edit and the
                  // backend then overrides is the same lie as a stale one.
                  disabled={running || engineOwnsCtsRules}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    engineOwnsCtsRules
                      ? 'Forced by the selected execution engine — pick Legacy to change it.'
                      : CTS_DIRECTION_RULES.find((r) => r.value === ctsDirection)?.hint || ''
                  }
                  sx={{ width: 250 }}
                >
                  {CTS_DIRECTION_RULES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </TextField>
              )}
              {/*
                TRENDPULSE'S LEVERS, shown only when a TrendPulse signal can actually be raised.
                Under CTS they would be controls with no effect, which is worse than absent ones --
                the same rule the CTS direction selector above follows.
              */}
              {(signalProvider === 'TRENDPULSE' || signalProvider === 'CONFLUENCE')
                && TRENDPULSE_LEVERS.map((lever) => (
                  <TextField
                    key={lever.key}
                    type="number"
                    label={lever.label}
                    size="small"
                    value={tpLevers[lever.key]}
                    onChange={(e) => {
                      const raw = Number(e.target.value);
                      // Blank or non-numeric falls back to the lever's own default rather than
                      // sending NaN, which would reach the API as an unparseable parameter and be
                      // silently dropped -- running the default while the box showed something
                      // else.
                      setTpLevers((prev) => ({
                        ...prev,
                        [lever.key]: Number.isFinite(raw) ? raw : lever.default,
                      }));
                    }}
                    disabled={running}
                    inputProps={{ min: lever.min, max: lever.max, step: lever.step }}
                    InputLabelProps={{ shrink: true }}
                    helperText={lever.hint}
                    sx={{ width: 230 }}
                  />
                ))}
              {(signalProvider === 'TRENDPULSE' || signalProvider === 'CONFLUENCE') && (
                <FormControlLabel
                  control={<Switch checked={tpDriftFilter} size="small" disabled={running}
                    onChange={(e) => setTpDriftFilter(e.target.checked)} />}
                  label="Drift filter"
                />
              )}
              <Button
                variant="contained"
                startIcon={running && !streaming ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={runFull}
                disabled={running || !symbol.trim()}
              >
                Run Full Simulation
              </Button>
              {streaming ? (
                <Button variant="outlined" color="warning" startIcon={<StopIcon />} onClick={stop}>
                  Stop
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<SkipNextIcon />}
                  onClick={runStepped}
                  disabled={running || !symbol.trim()}
                >
                  Run Step-by-Step
                </Button>
              )}

              <Box sx={{ width: 260 }}>
                <Typography variant="caption" color="text.secondary">
                  Step delay — {delayMs} ms per bar
                </Typography>
                <Slider
                  value={delayMs}
                  onChange={(_, v) => setDelayMs(v)}
                  min={0}
                  max={10000}
                  step={50}
                  marks={DELAY_MARKS}
                  size="small"
                  disabled={streaming}
                />
              </Box>

              <FormControlLabel
                control={<Switch checked={showNarratives} size="small"
                  onChange={(e) => setShowNarratives(e.target.checked)} />}
                label="Trade narratives"
              />
              <FormControlLabel
                control={<Switch checked={ctsReentry} size="small" disabled={running}
                  onChange={(e) => setCtsReentry(e.target.checked)} />}
                label="Second-chance re-entry"
              />
              {/*
                HIDDEN for a PTA or PTB run. The server FORCES both targets off for those
                providers -- they are CTS-tuned machinery and the band ruleset names no target --
                so leaving the switches on screen would show a control that cannot affect the run.
                A control that lies is worse than an absent one, which is the rule the direction
                selector and the TrendPulse levers already follow.
              */}
              {/* THE STRUCTURAL ENTRY FILTERS. Shown for every provider, because the question
                  "is my provider running alongside the breakout" applies to all of them, and
                  because this was a hardcoded value in the request until now -- invisible, and
                  therefore untestable. */}
              {/* WHICH STORED SERIES THE RUN LOADS. On screen rather than a hidden constant --
                  the version this replaces sent daily on every path, which made the intraday
                  bailout unable to fire and produced a "limit of the data" warning pointing at
                  files that were present all along. */}
              <TextField
                select size="small" label="Bar set" value={barSet}
                disabled={running || engineOwnsBarSet}
                onChange={(e) => setBarSet(e.target.value)}
                sx={{ width: 260, mb: 1 }}
                helperText={engineOwnsBarSet
                  ? 'FORCED by the selected engine — its validated figure is a daily run. Pick Legacy to sweep the bar set.'
                  : barSet === 'all'
                    ? 'All stored series — intraday exits can fire. What the published figures were measured on. The replay still steps DAILY bars.'
                    : 'Daily only — faster, and the 30m/65m trailing-floor bailout CANNOT fire.'}
              >
                <MenuItem value="all">All stored series (daily + 5m/30m/65m)</MenuItem>
                <MenuItem value="daily">Daily only</MenuItem>
              </TextField>
              <FormControlLabel
                control={<Switch checked={isolateProvider} size="small" disabled={running}
                  onChange={(e) => setIsolateProvider(e.target.checked)} />}
                label="Isolate provider (no breakout)"
              />
              {/* THE MACRO GATE, now shown for CTS as well as the band systems.
                  It used to be band-only on the reasoning that "the macro bypass is CTS's own
                  flag" -- true, and the wrong conclusion: CTS HAS a gate, it was simply
                  unreachable. There was no request parameter for it and not even a CLI flag that
                  turned it on, so the only honest thing the dialog could do was hide the switch.
                  Both are wired now and the switch means the same thing on every provider that
                  has one.

                  Still hidden for TrendPulse, where the override is granted per SHAPE rather than
                  per run, so a single switch could not express it. */}
              {hasMacroGate && (
              <FormControlLabel
                control={<Switch checked={macroGate} size="small" disabled={running}
                  onChange={(e) => setMacroGate(e.target.checked)} />}
                label="Macro gate (50/200)"
              />
              )}
              {isTimeBandProvider && (
              <FormControlLabel
                control={<Switch checked={bandVolumeConfluence} size="small" disabled={running}
                  onChange={(e) => setBandVolumeConfluence(e.target.checked)} />}
                label="Volume confluence"
              />
              )}
              {/* ── THE CTS EXIT SANDBOX ────────────────────────────────────────────────────
                  CTS ONLY, and the engine agrees: SignalProviderProfile forces ctsExitSandbox
                  false for PTA and PTB, so none of these can reach a band run however they are
                  set. Showing them on a band provider would be a block of controls that cannot
                  affect the run -- the thing this dialog has now been corrected for twice. */}
              {isCtsProvider && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    CTS Entry Gate
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}>
                    The node ARMS a setup; a later session&apos;s price TRIGGERS it. Bypassed
                    nodes are counted separately in the run summary, which is how the filter is
                    judged. <strong>The gate is ON by default here and OFF in the engine</strong>,
                    so this dialog does not reproduce the locked CTS baseline — untick it for
                    that. CTS only: the gate lives in the CTS provider, so no band run can reach
                    it however these are set.
                    {engineOwnsCtsRules && ' These are READ-ONLY right now: the selected '
                      + 'execution engine forces them, which is what makes its published figure '
                      + 'reproduce from its own name. Pick Legacy to set them by hand.'}
                  </Typography>
                  <LeverRows levers={CTS_ENTRY_LEVERS} values={ctsEntry}
                    onChange={setCtsEntryLever} running={running || engineOwnsCtsRules}
                    prefix="cts-entry" />
                </Box>
              )}

              {isCtsProvider && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    CTS Exit Strategy
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}>
                    Treats CTS as an INFLECTION signal: a dedicated exit manager replacing the
                    band systems&apos; ratcheting gates. All off reproduces the locked CTS
                    baseline. PTA and PTB are code-frozen and cannot reach any of this.
                  </Typography>
                  {/* READ-ONLY while an engine owns them, exactly as the entry levers and the
                      direction dropdown are. A switch a reader can move over a value the
                      backend overrides is the same lie as a stale one. */}
                  <LeverRows levers={CTS_EXIT_LEVERS} values={ctsExit}
                    onChange={setCtsLever} running={running || engineOwnsCtsRules}
                    prefix="cts-exit" />
                </Box>
              )}
              {/* ── EXIT PARAMETERS: profit protection ───────────────────────────────────
                  Shown for EVERY provider, unlike the CTS panel above. These are stop-management
                  rules rather than one engine's own mechanics, and they are polled on all three
                  exit surfaces -- including ATR_TRAILING_FLOOR, the engine default, which
                  originally polled neither manager so the switch silently did nothing. */}
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Exit parameters (profit protection)
                </Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}>
                  Both OFF reproduces every published figure exactly — asserted by a regression
                  guard and verified on real data by diffing the full CTS, PTA and PTB ledgers
                  either side of the change.
                </Typography>
                <LeverRows levers={EXIT_PROTECTION_LEVERS} values={protection}
                  onChange={setProtectionLever} running={running} prefix="protection" />
              </Box>

              {/* ── COMPOSITE sub-toggles, revealed only for that provider ──────────────── */}
              {isComposite && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Composite engines
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}>
                    Each runs its own state machine over the same bars and holds its own premise,
                    so their positions OVERLAP. The merged ledger tags every trade with the
                    engine that took it, and the net figures are not a portfolio model — the run
                    warns about that rather than leaving it to be inferred.
                  </Typography>
                  {COMPOSITE_ENGINES.map((engine) => (
                    <FormControlLabel
                      key={engine.key}
                      control={(
                        <Switch size="small" disabled={running}
                          data-testid={`composite-${engine.key}`}
                          checked={!!compositeEngines[engine.key]}
                          onChange={(e) => setCompositeEngines((prev) => ({
                            ...prev, [engine.key]: e.target.checked }))} />
                      )}
                      label={engine.label}
                    />
                  ))}
                </Box>
              )}

              {/* ── PROFIT TARGETS, in their own block ────────────────────────────────────
                  These are PRICE targets -- the first and second structural swing levels beyond
                  the entry -- and have nothing to do with the PTA and PTB time-band engines.
                  The names collide and the controls used to sit immediately after the CTS Exit
                  panel with no heading between them, which read as a PTA control leaking into
                  the CTS view. It was never a leak; it was a name clash with no boundary drawn.

                  The boundary is drawn now, and the heading says what they actually are. */}
              {!isTimeBandProvider && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Profit targets (structural price levels)
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}>
                    Swing levels beyond the entry. Nothing to do with the PTA/PTB time-band
                    engines, which happen to share the letters.
                  </Typography>
                  {/* THE FIRST-LEVEL TARGET IS HIDDEN, on request, and soft-deleted rather than
                      removed: the state, the payload field and the engine lever are all intact,
                      and the shipped CTS strategy has it OFF anyway (the first structural level
                      is a median 1.09R from a CTS entry, so scaling out there flattens the
                      asymmetry the strategy runs on -- +32.01% against +62.20%). Flip this one
                      constant to bring the control back. */}
                  {SHOW_FIRST_LEVEL_TARGET && (
                  <FormControlLabel
                    control={<Switch checked={targetPta} size="small" disabled={running}
                      onChange={(e) => setTargetPta(e.target.checked)} />}
                    label="1st structural level"
                  />
                  )}
                  {/* GREYED while an engine owns it, like the direction dropdown, the bar set
                      and the CTS levers. The Timing Engine's surface is the trailing floor and
                      the stop; a target it inherited from the standalone CTS default was worth
                      30 of its 162 exits. */}
                  <FormControlLabel
                    control={<Switch checked={targetPtb} size="small"
                      disabled={running || engineOwnsTargets}
                      onChange={(e) => setTargetPtb(e.target.checked)} />}
                    label="2nd structural level (the shipped target)"
                  />
                </Box>
              )}
              {!isTimeBandProvider && (targetPta || targetPtb) && (
                <TextField
                  type="number"
                  label="Level min distance (R)"
                  size="small"
                  value={levelMinR}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    // Falls back to 0 -- the engine default -- rather than sending NaN, which the
                    // API would drop silently and run the default while the box showed something
                    // else.
                    setLevelMinR(Number.isFinite(raw) && raw >= 0 ? raw : 0);
                  }}
                  disabled={running}
                  inputProps={{ min: 0, max: 10, step: 0.5 }}
                  InputLabelProps={{ shrink: true }}
                  helperText="2.0 enforces the payoff asymmetry. On SPY the first level is a
                    median 1.09R out and 43% are inside 1R, so 0 aims at noise; every run measured
                    without a floor lost to having no targets at all."
                  sx={{ width: 230 }}
                />
              )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Reachability is surfaced prominently, not logged. A run missing its intraday series
                produces a ledger in which one whole exit rule never fires, and that reads as a
                finding about the rules unless it is said out loud. */}
            {warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>This data cannot exercise every rule</AlertTitle>
                {warnings.map((w) => <div key={w}>{w}</div>)}
              </Alert>
            )}

            {/* WHAT THIS RUN IS, from the backend's own profile -- the same sentence the CLI
                prints in its run header, so the app and the backtest cannot describe one
                configuration two ways.

                severity="info" and NOT folded into `warnings` above: this is a statement of
                configuration, not a problem. Putting it in the warnings alert would make a
                correct run look faulty, and a reader would learn to skim past the one place real
                warnings appear. */}
            {result?.configuration && (
              <Alert severity="info" sx={{ mb: 2 }} data-testid="run-configuration">
                <AlertTitle>What this run is</AlertTitle>
                {result.configuration}
              </Alert>
            )}

            {result?.meta && (
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" variant="outlined"
                  label={`${result.meta.barsReplayed.toLocaleString()} bars`} />
                {Object.entries(result.meta.barsByTimeframe || {}).map(([tf, n]) => (
                  <Chip key={tf} size="small" variant="outlined"
                    label={`${tf}: ${n.toLocaleString()}`} />
                ))}
                <Chip size="small" variant="outlined" label={`ended ${result.meta.finalState}`} />
                <Chip size="small" variant="outlined" label={`${result.meta.elapsedMillis} ms`} />
              </Stack>
            )}

            <Divider sx={{ mb: 2 }} />

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Summary" />
              <Tab label={`Ledger${result ? ` (${result.completedTrades?.length || 0})` : ''}`} />
              <Tab label={`Signal Audit${totalDeclined ? ` (${totalDeclined})` : ''}`} />
              <Tab label="Live" />
            </Tabs>

            {tab === 0 && (
              result
                ? (
                  <>
                    {/* The TRADES, not a statistics block: this panel and the Running Ledger
                        fold one list through one reducer so they cannot report different
                        numbers for the same run. See utils/portfolioMetrics. */}
                    <DynaManStatsPanel trades={result.completedTrades}
                      initialCapital={DEBUG_ACCOUNT.startingEquity}
                      rejectionCount={totalDeclined} />
                    {/* THE ANNUAL BREAKDOWN, directly under the figures it decomposes. Derived
                        from the run's own trades, so a re-run with tweaked parameters rebuilds
                        it with no wiring -- and its All row is computed by the same code over
                        every trade, which is what makes it checkable against the panel above. */}
                    <DynaManAnnualTable
                      trades={result.completedTrades}
                      symbol={result.symbol || symbol}
                      provider={signalProvider}
                      // THE BACKEND'S OWN PER-YEAR ROWS, for the two columns this table cannot
                      // derive: bypassed nodes come from the rejection log and points need the
                      // account's fills. Everything the two both know still comes from the ONE
                      // JS calculator, so they cannot disagree.
                      engineRows={result.annualSummary || []}
                    />
                  </>
                )
                : <Typography variant="body2" color="text.secondary">
                    Run a simulation to see aggregate statistics.
                  </Typography>
            )}

            {tab === 1 && (
              result
                ? <DynaManLedgerTable
                    completedTrades={result.completedTrades || []}
                    openTrade={result.openTrade}
                    showNarratives={showNarratives}
                  />
                : <Typography variant="body2" color="text.secondary">
                    Run a simulation to see the trade ledger.
                  </Typography>
            )}

            {tab === 2 && (
              <DynaManAuditTable rejections={auditRows} rejectionCounts={auditCounts} />
            )}

            {tab === 3 && (
              <DynaManLiveHud
                bar={liveBar}
                transitions={liveTransitions}
                streaming={streaming}
                rejectionCount={liveRejections.length}
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {/* The live view is a separate, full-screen surface rather than a fifth tab here: it needs
            the whole viewport for the chart, and this dialog is boxed. */}
        <Button onClick={() => setLiveOpen(true)} disabled={!symbol}>
          Watch live
        </Button>
        {/* VISUAL VALIDATION. The tables say what happened; only the chart says whether it was
            right, and an intraday bailout in particular is a claim about a bar you have to look
            at. Disabled until a run exists, because there is nothing to draw before one.

            It CLOSES the dialog. The chart it draws on is behind this dialog, so leaving it open
            would put the answer underneath the question — and the markers are the whole point. */}
        {onShowOnChart && (
          <Button
            variant="contained"
            startIcon={<ShowChartIcon />}
            disabled={!result?.completedTrades?.length}
            onClick={() => { onShowOnChart(result); onClose(); }}
          >
            Show on Chart
          </Button>
        )}
        {onStartReplay && (
          <Button
            variant="contained"
            color="warning"
            startIcon={loadingReplay ? <CircularProgress size={16} /> : <MovieFilterIcon />}
            disabled={!symbol || loadingReplay}
            onClick={startReplay}
          >
            {loadingReplay ? 'Loading…' : 'Replay on Chart'}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <DynaManLiveSimulatorDialog
        open={liveOpen}
        symbol={symbol}
        initialProvider={signalProvider}
        customSeries={customSeries}
        onClose={() => setLiveOpen(false)}
      />
    </Dialog>
  );
};

DynaManSimulatorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialSymbol: PropTypes.string,
  /**
   * The customer's own parsed series, or null for a run on stored data.
   *
   * <p>Held by App.js rather than here, because the CHART is drawn from the same object: one
   * source for the candles and for the engine, so a replay cannot run over bars the chart is not
   * showing.
   */
  // eslint-disable-next-line react/forbid-prop-types
  customSeries: PropTypes.object,
  /**
   * Hand the finished simulation result to the chart. Optional: absent, the button is not
   * rendered at all rather than rendered inert, so a host that has no chart to draw on does not
   * show a control that cannot do anything.
   */
  onShowOnChart: PropTypes.func,
  /**
   * Hand a bar-by-bar timeline to the chart's replay engine. Optional: absent, the control is not
   * rendered rather than rendered inert.
   */
  onStartReplay: PropTypes.func,
};

export default DynaManSimulatorDialog;
